import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import EventDropAttribution from '../components/EventDropAttribution';
import { FilmStrip, Play, CalendarBlank, Image as ImageIcon, ShareNetwork } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Public, view-only event share page.
 * URL: /share/:token
 * No auth required. Shows gallery, film (if ready), highlight (if ready).
 */
const PublicShare = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeMedia, setActiveMedia] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [infoRes, photosRes] = await Promise.all([
          axios.get(`${API}/public/events/${token}`),
          axios.get(`${API}/public/events/${token}/photos?limit=200`),
        ]);
        setInfo(infoRes.data);
        setPhotos(photosRes.data);
      } catch (e) {
        setError(e.response?.status === 404 ? 'This share link is no longer active.' : 'Failed to load event.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white/60 text-sm">Loading event…</div>
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6">
        <div className="text-center" data-testid="share-error">
          <ShareNetwork size={48} weight="thin" className="text-white/20 mx-auto mb-4" />
          <p className="text-white text-lg font-serif mb-1">{error || 'Not found'}</p>
          <p className="text-white/40 text-sm">The host may have disabled sharing.</p>
        </div>
      </div>
    );
  }

  const filmUrl = `${API}/public/events/${token}/film/stream`;
  const highlightUrl = `${API}/public/events/${token}/highlight/stream`;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="public-share-page">
      {/* Header */}
      <header className="px-6 py-8 border-b border-white/5" style={{ background: 'linear-gradient(180deg, #1a1a2e, #0a0a1a)' }}>
        <div className="max-w-4xl mx-auto">
          <h1 className="font-serif text-3xl sm:text-4xl font-bold mb-2" data-testid="share-event-name">{info.event_name}</h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-white/60">
            {info.event_date && (
              <span className="flex items-center gap-1.5">
                <CalendarBlank size={14} weight="bold" /> {info.event_date}
              </span>
            )}
            {info.host_name && <span>Hosted by {info.host_name}</span>}
            <span className="flex items-center gap-1.5 ml-auto">
              <ImageIcon size={14} weight="bold" /> {photos.length} {photos.length === 1 ? 'memory' : 'memories'}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-10">
        {/* Highlight */}
        {info.has_highlight && (
          <section data-testid="share-highlight-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl font-bold flex items-center gap-2">
                <Play weight="fill" /> Highlight Reel
              </h2>
              {info.highlight_duration && <span className="text-white/40 text-sm">{info.highlight_duration}</span>}
            </div>
            <div className="rounded-xl overflow-hidden bg-black mx-auto" style={{ maxWidth: 360, aspectRatio: '9/16' }}>
              <video src={highlightUrl} controls playsInline className="w-full h-full object-contain" data-testid="share-highlight-video" />
            </div>
          </section>
        )}

        {/* Film */}
        {info.has_film && (
          <section data-testid="share-film-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl font-bold flex items-center gap-2">
                <FilmStrip weight="bold" /> Event Film
              </h2>
              {info.film_duration && <span className="text-white/40 text-sm">{info.film_duration}</span>}
            </div>
            <div className="rounded-xl overflow-hidden bg-black mx-auto" style={{ maxWidth: 360, aspectRatio: '9/16' }}>
              <video src={filmUrl} controls playsInline className="w-full h-full object-contain" data-testid="share-film-video" />
            </div>
          </section>
        )}

        {/* Gallery */}
        <section data-testid="share-gallery-section">
          <h2 className="font-serif text-xl font-bold mb-4">Gallery</h2>
          {photos.length === 0 ? (
            <p className="text-white/40 text-sm">No photos yet — check back soon.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-1.5" data-testid="share-gallery-grid">
              {photos.map((p) => {
                const mediaUrl = `${BACKEND_URL}${p.image_url}`;
                const isVideo = (p.content_type || '').startsWith('video/');
                return (
                  <button
                    key={p.photo_id}
                    onClick={() => setActiveMedia({ url: mediaUrl, isVideo })}
                    className="relative aspect-square bg-white/5 rounded-lg overflow-hidden group hover:ring-2 hover:ring-[#B89762] transition-all"
                    data-testid={`gallery-item-${p.photo_id}`}
                  >
                    {isVideo ? (
                      <>
                        <video src={mediaUrl} className="w-full h-full object-cover" muted preload="metadata" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/10">
                          <Play weight="fill" className="text-white drop-shadow-lg" size={24} />
                        </div>
                      </>
                    ) : (
                      <img src={mediaUrl} alt="" loading="lazy" className="w-full h-full object-cover" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </section>
      </main>

      {/* Lightbox */}
      {activeMedia && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setActiveMedia(null)}
          data-testid="share-lightbox"
        >
          {activeMedia.isVideo ? (
            <video src={activeMedia.url} controls autoPlay playsInline className="max-w-full max-h-full" onClick={(e) => e.stopPropagation()} />
          ) : (
            <img src={activeMedia.url} alt="" className="max-w-full max-h-full object-contain" />
          )}
          <a
            href={activeMedia.url}
            download
            onClick={(e) => e.stopPropagation()}
            className="absolute top-4 right-16 px-4 py-2 rounded-lg bg-white/10 backdrop-blur-sm text-white text-sm font-semibold hover:bg-white/20 transition-colors"
            data-testid="lightbox-download"
          >
            ↓ Download
          </a>
          <button
            onClick={(e) => { e.stopPropagation(); setActiveMedia(null); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-lg bg-white/10 backdrop-blur-sm text-white hover:bg-white/20"
            data-testid="lightbox-close"
          >×</button>
        </div>
      )}

      {/* VIRAL HOOK — every guest viewing a shared gallery sees the EventDrop pitch */}
      <div className="mx-auto max-w-xl px-4 mt-4 mb-2" data-testid="viral-hook">
        <div className="rounded-2xl p-6 text-center" style={{ background: 'linear-gradient(135deg, rgba(255,107,107,0.18), rgba(255,204,0,0.10))', border: '1px solid rgba(255,204,0,0.25)' }}>
          <p className="text-[11px] font-black uppercase tracking-[0.2em] mb-2" style={{ background: 'linear-gradient(135deg, #FF6B6B, #FFCC00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Love this?</p>
          <h3 className="text-lg font-black text-white mb-2">Start yours in 60 seconds</h3>
          <p className="text-xs text-gray-400 mb-4">Collect every photo your guests take — no app required.</p>
          <a href="https://eventdrop.co/pricing" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-black" style={{ background: 'linear-gradient(135deg, #FF6B6B, #FFCC00)' }} data-testid="viral-hook-cta">See how it works →</a>
        </div>
      </div>

      <footer className="text-center text-white/30 text-xs py-8">
        Built with EventDrop · Private share link
      </footer>
      <EventDropAttribution />
    </div>
  );
};

export default PublicShare;
